import { useCallback } from 'react';
import styled from 'styled-components';
import BaseFormattedNumber, {
  NumberFormatValues,
  SourceInfo,
  NumberFormatProps,
} from 'react-number-format';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';
import { useHiddenLimitOrderFeatureFlag } from '@paradigm/features/src/feature-flags/context';

import Toggle from '@paradigm/design-system/src/components/Toggle';
import { pickledBluewood } from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

export default function HiddenLimitOrderFields() {
  const { hiddenLimitOrder } = useRfqBuilderView();
  const {
    hiddenLimitOrderToggle,
    hiddenLimitOrderBidPriceChange,
    hiddenLimitOrderAskPriceChange,
  } = useRfqBuilderActions();
  const isHiddenLimitOrderEnabled = useHiddenLimitOrderFeatureFlag();

  const handleToggleChange = useCallback(() => {
    hiddenLimitOrderToggle(!hiddenLimitOrder.enabled);
  }, [hiddenLimitOrderToggle, hiddenLimitOrder.enabled]);

  const handleBidPriceChange = useCallback(
    (values: NumberFormatValues, _sourceInfo: SourceInfo) => {
      hiddenLimitOrderBidPriceChange(values.formattedValue || '');
    },
    [hiddenLimitOrderBidPriceChange],
  );

  const handleAskPriceChange = useCallback(
    (values: NumberFormatValues, _sourceInfo: SourceInfo) => {
      hiddenLimitOrderAskPriceChange(values.formattedValue || '');
    },
    [hiddenLimitOrderAskPriceChange],
  );

  if (!isHiddenLimitOrderEnabled || !hiddenLimitOrder.showFields) {
    return null;
  }

  return (
    <Container>
      <Toggle
        ariaLabel="Hidden Limit Order"
        label="Hidden Limit Order"
        checked={hiddenLimitOrder.enabled}
        onChange={handleToggleChange}
      />
      {hiddenLimitOrder.enabled && (
        <TwoWayContainer>
          <InputContainer>
            <FormattedNumber
              data-testid="hidden-bid-price"
              onValueChange={handleBidPriceChange}
              value={hiddenLimitOrder.bidPrice}
              decimalScale={4}
              allowNegative={false}
              placeholder="Bid Price"
            />
          </InputContainer>
          <Separator>/</Separator>
          <InputContainer>
            <FormattedNumber
              data-testid="hidden-ask-price"
              onValueChange={handleAskPriceChange}
              value={hiddenLimitOrder.askPrice}
              decimalScale={4}
              allowNegative={false}
              placeholder="Ask Price"
            />
          </InputContainer>
        </TwoWayContainer>
      )}
    </Container>
  );
}

const FormattedNumber = (props: NumberFormatProps) => (
  <BaseFormattedNumber
    thousandSeparator
    css="flex: 1; width: 100%;"
    {...props}
  />
);

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const TwoWayContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 3px;
  height: 32px;
  background: ${pickledBluewood};
  min-width: 200px;

  input {
    color: white;
    border: none;
    background: transparent;
    padding: 0px 10px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
      font-size: 0.75rem;
    }
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: 180px;

    input:first-child {
      text-align: right;
    }
  }
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  height: 100%;
  position: relative;
`;

const Separator = styled.span`
  color: white;
  margin: 0 0.25rem;
  font-size: 0.875rem;
`;
